import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as yup from 'yup';
import _debounce from 'lodash/debounce';

// Components
import Header from '../components/reusable/Header';
import BackButton from '../components/reusable/buttons/BackButton';
import { BigButtons } from '../components/reusable/buttons/Buttons';
import RadioGroup from '../components/pageComponents/Custom/RadioGroup';
import SelectWithPicture from '../components/reusable/SelectWithPicture';

import { dispatch } from '../redux/store';

// Utils
import { notifyError, removeEmpty } from '../utils/helpers';

// Gender options for work status
const GENDER_OPTIONS = [
    { value: 'MEN', label: "Men's football" },
    { value: 'WOMEN', label: "Women's football" },
    { value: 'BOTH', label: 'Both' }
];

const WorkStatus = () => {
    const navigate = useNavigate();
    const userInfo = useSelector(({ auth }) => auth?.authUser?.userInfo);

    // Club and team selection state
    const [teams, setTeams] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [selectTeam, setSelectTeam] = useState({});
    const [selectedClub, setSelectedClub] = useState({});
    const [errorMessage, setNotFoundClubError] = useState(false);
    const [isFetchingTeams, setIsFetchingTeams] = useState(false);

    // Loading state for user update
    const loadingUserUpdate = useSelector(
        ({ loading }) => loading.effects.user.updateUser
    );

    // Dynamic validation schema based on selected club
    const getValidationSchema = useCallback(() => {
        return yup.object().shape({
            gender: yup
                .string()
                .required('Please select which football you work in'),
            clubName: yup.string().notRequired(),
            teamName: yup.string().when('clubName', {
                is: val => {
                    // If no club selected, team is not required
                    if (!val) return false;
                    // If club is a COMPANY, team is not required
                    if (selectedClub?.typeOfOrg === 'COMPANY') return false;
                    // Otherwise, team is required when club is selected
                    return true;
                },
                then: () => yup.string().required('Please select a team'),
                otherwise: () => yup.string().notRequired()
            })
        });
    }, [selectedClub]);

    // Formik setup
    const formik = useFormik({
        initialValues: {
            clubName: userInfo?.clubName || '',
            teamName: userInfo?.teamName || '',
            gender: userInfo?.gender || ''
        },
        validationSchema: getValidationSchema(),
        validateOnMount: false,
        validateOnChange: false,
        validateOnBlur: true,
        onSubmit: values => {
            handleFormSubmit(values);
        }
    });

    const {
        values,
        errors,
        touched,
        dirty,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue
    } = formik;

    const handleTeamChange = useCallback(
        ({ target }) => {
            const selectedTeam =
                teams.find(item =>
                    item?.teamName
                        ?.toLowerCase()
                        ?.includes(target.value?.toLowerCase())
                ) || {};

            setSelectTeam(selectedTeam);
            setFieldValue('teamName', target.value);
        },
        [teams, setFieldValue]
    );

    const handleClubSelection = useCallback(
        async clubId => {
            setIsFetchingTeams(true);
            const teamsInGivenClub = [];
            const res = await dispatch.team.getTeamsByClubId(clubId);
            if (res?.data) {
                teamsInGivenClub.push(...res.data);
            }

            if (teamsInGivenClub?.length === 1) {
                setSelectTeam(teamsInGivenClub[0]);
                setFieldValue('teamName', teamsInGivenClub[0]?.teamName);
            }
            setTeams(teamsInGivenClub);
            setIsFetchingTeams(false);
        },
        [setFieldValue]
    );

    const getTeamsBasedOnSelectedClub = useCallback(
        clubId => {
            const debouncedFetch = _debounce(handleClubSelection, 100);
            debouncedFetch(clubId);
        },
        [handleClubSelection]
    );

    const handleSetSelectedClub = useCallback(
        club => {
            setSelectedClub(club);
            setFieldValue('clubName', club?.clubName || '');
            if (club?.id) {
                getTeamsBasedOnSelectedClub(club.id);
            } else {
                setTeams([]);
                setSelectTeam({});
                setFieldValue('teamName', '');
            }
        },
        [setFieldValue, getTeamsBasedOnSelectedClub]
    );

    const handleFormSubmit = useCallback(
        async values => {
            try {
                // If form is not dirty (no changes), navigate directly
                if (!dirty) {
                    navigate('/account-setup');
                    return;
                }

                // Prepare data for submission
                const mergedValues = {
                    ...values,
                    id: userInfo.id,
                    teamId: selectTeam?.id || '',
                    clubId: selectedClub?.id || ''
                };

                // Clean empty values
                const cleanData = removeEmpty(mergedValues);

                // Submit the form data
                const res = await dispatch.user.updateUser(cleanData);

                if (res === 1) {
                    // Refresh user data
                    await Promise.all([
                        dispatch.user.getUserProfile({ id: userInfo?.id }),
                        dispatch.user.userSearchByProjection()
                    ]);

                    navigate('/account-setup');
                }
            } catch {
                notifyError(
                    'Failed to update work status information. Please try again.'
                );
            }
        },
        [dirty, userInfo, selectTeam, selectedClub, navigate]
    );

    // Update validation schema when selectedClub changes
    useEffect(() => {
        formik.setFormikState(state => ({
            ...state,
            validationSchema: getValidationSchema()
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClub, getValidationSchema]);

    // Update club name in formik when club is selected
    useEffect(() => {
        if (selectedClub?.clubName) {
            setFieldValue('clubName', selectedClub.clubName);
        } else if (!selectedClub?.clubName && values.clubName) {
            // Clear clubName if no club is selected
            setFieldValue('clubName', '');
        }
    }, [selectedClub, setFieldValue, values.clubName]);

    // Update team name in formik when team is selected
    useEffect(() => {
        if (selectTeam?.teamName) {
            setFieldValue('teamName', selectTeam.teamName);
        } else if (!selectTeam?.teamName && values.teamName) {
            // Clear teamName if no team is selected
            setFieldValue('teamName', '');
        }
    }, [selectTeam, setFieldValue, values.teamName]);

    // Reset teams when gender changes (similar pattern to ContractSelect)
    const prevGenderRef = useRef(values.gender);
    useEffect(() => {
        // Only reset if gender actually changed (not on initial render)
        if (prevGenderRef.current && prevGenderRef.current !== values.gender) {
            // Reload clubs when gender changes
            setFieldValue('clubName', '');
            setFieldValue('teamName', '');
            setSelectedClub({});
            setSelectTeam({});
            setTeams([]);
        }
        prevGenderRef.current = values.gender;
    }, [values.gender, setFieldValue]);

    const handleBack = () => {
        navigate(-1);
    };

    // Initialize with existing user data
    useEffect(() => {
        // Initialize selected club if exists in userInfo
        if (userInfo?.clubName && userInfo?.clubId) {
            setSelectedClub({
                id: userInfo.clubId,
                clubName: userInfo.clubName
            });
        }

        // Initialize selected team if exists in userInfo
        if (userInfo?.teamName && userInfo?.teamId) {
            setSelectTeam({
                id: userInfo.teamId,
                teamName: userInfo.teamName
            });
        }
    }, [userInfo]);

    // Load clubs when gender is selected
    useEffect(() => {
        const loadClubs = async () => {
            if (values.gender) {
                await dispatch.club.getClubsByGenderAndType({
                    gender: values.gender
                });
            }
        };
        loadClubs();
    }, [values.gender]);

    return (
        <div className='min-h-screen bg-white page mt-5'>
            {/* Header with back button */}
            <Header>
                <BackButton onClick={handleBack} />
                <div></div>
            </Header>

            <form onSubmit={handleSubmit}>
                <div className='px-2 pb-32 mt-10'>
                    {/* Gender Selection */}
                    <div className='mb-12'>
                        <h2 className='text-[18px] font-bold text-black uppercase mb-6 font-favela-bold'>
                            I WORK IN...
                        </h2>
                        <RadioGroup
                            name='gender'
                            value={values.gender}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            options={GENDER_OPTIONS}
                            direction='col'
                            className='space-y-4'
                            size='lg'
                        />
                        {errors.gender && touched.gender && (
                            <p className='text-red-600 text-xs mt-2'>
                                {errors.gender}
                            </p>
                        )}
                    </div>

                    {/* Football Club Section */}
                    {values.gender && (
                        <div className='relative w-full mb-7 group'>
                            <h2 className='text-[18px] font-bold text-black uppercase mb-4 font-favela-bold'>
                                I WORK FOR...
                            </h2>
                            <label
                                htmlFor='clubs'
                                className={`text-[14px] ${errors.clubName && touched.clubName ? 'text-red-600' : 'text-gray-500'}`}
                            >
                                Football Club/Organisation
                            </label>
                            <div className='flex items-center relative'>
                                <div className='appearance-none border-none w-full text-gray-700 py-1 leading-tight'>
                                    <SelectWithPicture
                                        userInfo={{
                                            ...userInfo,
                                            gender: values.gender
                                        }}
                                        setSelectClub={handleSetSelectedClub}
                                        selectClub={selectedClub}
                                        setTeams={setTeams}
                                        clubsByGender
                                        borderColor={
                                            errors.clubName && touched.clubName
                                                ? 'border-red-600'
                                                : ''
                                        }
                                        setSelectTeam={setSelectTeam}
                                        setNotFoundClubError={
                                            setNotFoundClubError
                                        }
                                        searchValue={searchValue}
                                        setSearchValue={setSearchValue}
                                        getTeamsBasedOnSelectedClub={
                                            getTeamsBasedOnSelectedClub
                                        }
                                        onBlur={() =>
                                            handleBlur({
                                                target: { name: 'clubName' }
                                            })
                                        }
                                    />
                                </div>
                            </div>
                            {errors.clubName &&
                                touched.clubName &&
                                !searchValue && (
                                    <p className='text-red-600 text-xs mt-2'>
                                        {errors.clubName}
                                    </p>
                                )}
                            {errorMessage && searchValue.length > 2 && (
                                <div className='text-red-600 font-poppins text-xs mt-2'>
                                    <p>Club not in database.</p>
                                    <p>
                                        Please contact{' '}
                                        <a
                                            className='text-red-600'
                                            href={`mailto:hello@playerapp.co?subject=Add club to database&body=Hello PLAYER, \n Please add '${searchValue}' to your system`}
                                        >
                                            hello@playerapp.co{' '}
                                        </a>
                                        to get it added.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Team Section */}
                    {teams?.length > 0 && (
                        <div className='relative w-full my-5'>
                            <label
                                htmlFor='teamName'
                                className={`text-[14px] ${errors.teamName && touched.teamName ? 'text-red-600' : 'text-gray-500'}`}
                            >
                                Team
                            </label>
                            <div className='flex items-center'>
                                <div className='appearance-none border-none w-full text-gray-700 py-1 leading-tight'>
                                    <select
                                        name='teamName'
                                        value={values.teamName}
                                        onChange={handleTeamChange}
                                        onBlur={handleBlur}
                                        className={`appearance-none bg-transparent border-b-2 ${errors.teamName && touched.teamName ? 'border-red-600 text-red-600' : 'border-gray-600 text-gray-700'} w-full py-1 pl-2 leading-tight focus:outline-none`}
                                    >
                                        <option value=''>Select team</option>
                                        {teams
                                            .sort(
                                                (a, b) =>
                                                    (b.index || 0) -
                                                    (a.index || 0)
                                            )
                                            .map((team, idx) => (
                                                <option
                                                    key={idx}
                                                    value={team?.teamName}
                                                >
                                                    {team?.teamName}
                                                </option>
                                            ))}
                                    </select>
                                </div>
                            </div>
                            {errors.teamName && touched.teamName && (
                                <p className='text-red-600 text-xs mt-1'>
                                    {errors.teamName}
                                </p>
                            )}
                        </div>
                    )}

                    {/* No teams found message */}
                    {selectedClub?.clubName &&
                        selectedClub?.typeOfOrg !== 'COMPANY' &&
                        teams.length === 0 &&
                        !isFetchingTeams && (
                            <div className='text-red-600 font-poppins text-xs mb-5'>
                                <p>
                                    No teams found for {selectedClub.clubName}.
                                </p>
                                <p>
                                    Please contact{' '}
                                    <a
                                        className='text-red-600'
                                        href={`mailto:hello@playerapp.co?subject=Add team to database&body=Hello PLAYER, \n Please add teams for '${selectedClub.clubName}' to your system`}
                                    >
                                        hello@playerapp.co{' '}
                                    </a>
                                    to get teams added for this club.
                                </p>
                            </div>
                        )}
                </div>

                {/* Fixed Next Button at bottom - only show when gender is selected */}
                {values.gender && (
                    <div className='fixed bottom-0 left-0 right-0 p-4'>
                        <BigButtons
                            black
                            label={loadingUserUpdate ? 'SAVING...' : 'NEXT'}
                            type='submit'
                            isDisabled={isFetchingTeams || loadingUserUpdate}
                            isLoading={loadingUserUpdate}
                        />
                    </div>
                )}
            </form>
        </div>
    );
};

export default WorkStatus;
